import { useState } from 'react';
import { formatDate } from '@/lib/utils';
import { DataSource } from '@/types/data-source';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Database,
  Globe,
  Edit,
  Copy,
  Trash2,
  Play,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  FileUp,
  BarChart3,
} from 'lucide-react';

interface DataSourcesTableProps {
  dataSources: DataSource[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onEdit: (dataSource: DataSource) => void;
  onDelete: (id: string) => void;
  onDuplicate: (dataSource: DataSource) => void;
  onTest: (id: string) => void;
  onExtract: (id: string) => void;
  onViewHistory: (dataSource: DataSource) => void;
  onPreview?: (dataSource: DataSource) => void;
}

const typeIcons: Record<string, any> = {
  DATABASE: Database,
  REST: Globe,
  GRAPHQL: Globe,
  TABLEAU: BarChart3,
  FILE_UPLOAD: FileUp,
};

const typeLabels: Record<string, string> = {
  DATABASE: 'Database',
  REST: 'REST',
  GRAPHQL: 'GraphQL',
  TABLEAU: 'Tableau',
  FILE_UPLOAD: 'File Upload',
};

const statusConfig = {
  active: {
    variant: 'success' as const,
    icon: CheckCircle2,
    label: 'Active',
  },
  inactive: {
    variant: 'secondary' as const,
    icon: AlertCircle,
    label: 'Inactive',
  },
};

export function getTotalExtractions(source: DataSource): number {
  return source.totalExtractions ?? source.stats?.totalExtractions ?? 0;
}

export default function DataSourcesTable({
  dataSources,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEdit,
  onDelete,
  onDuplicate,
  onTest,
  onExtract,
  onViewHistory,
  onPreview,
}: DataSourcesTableProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const allSelected =
    dataSources.length > 0 && dataSources.every((source) => selectedIds.has(source.id));

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this data source?')) {
      setDeletingId(id);
      onDelete(id);
      setTimeout(() => setDeletingId(null), 1000);
    }
  };

  if (dataSources.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Database className="h-12 w-12 mx-auto mb-4 opacity-20" />
        <p>No data sources configured yet.</p>
        <p className="text-sm">Click "Add Data Source" to get started.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => onToggleSelectAll(e.target.checked)}
              aria-label="Select all data sources"
              className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
            />
          </TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Extractions</TableHead>
          <TableHead>Last Extracted</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {dataSources.map((source) => {
          const TypeIcon = typeIcons[source.type] || Database;
          const status = source.isActive ? statusConfig.active : statusConfig.inactive;
          const StatusIcon = status.icon;
          const isSelected = selectedIds.has(source.id);

          return (
            <TableRow key={source.id}>
              <TableCell>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleSelect(source.id)}
                  aria-label={`Select ${source.name}`}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
                />
              </TableCell>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <TypeIcon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="flex items-center gap-2">
                      {source.name}
                      {source.sensitivity === 'PII' && (
                        <Badge
                          variant="outline"
                          className="border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                        >
                          PII
                        </Badge>
                      )}
                    </div>
                    {source.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {source.description}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {typeLabels[source.type] || source.type}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant={status.variant} className="flex items-center gap-1 w-fit">
                  <StatusIcon
                    className="h-3 w-3"
                  />
                  {status.label}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {getTotalExtractions(source)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {source.lastExtractedAt ? formatDate(source.lastExtractedAt) : 'Never'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onTest(source.id)}
                    title="Test Connection"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onExtract(source.id)}
                    title="Run Extraction"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                  {onPreview && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPreview(source)}
                      title="Preview Data"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewHistory(source)}
                    title="View Extraction History"
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(source)}
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDuplicate(source)}
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(source.id)}
                    disabled={deletingId === source.id}
                    title="Delete"
                  >
                    {deletingId === source.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
